import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useCallback, useEffect, useState } from 'react';
import { Menu, MenuButton, MenuItems } from '@headlessui/react';
import { twMerge } from 'tailwind-merge';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import Button from 'flow/components/ui-components/Button';
import Checkbox from 'flow/components/ui-components/Checkbox';
import Icon from 'flow/components/ui-components/Icon';
import MenuItem from 'flow/components/ui-components/MenuItem';
import { selectAllTagsAvailableForTicketTagging } from 'flow/selectors/TicketsSelectors';
import { fetchAllAvailableTags } from 'flow/actions/tickets-action';
import AssigneePicker from './AssigneePicker';
import SinceDatePicker from './SinceDatePicker';
import TagPicker from './TagPicker';
import { hasAnyAdvancedFilterActive } from './filter-utils';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var EMPTY_FILTERS = {
  marked: false,
  since: ''
};
var STATE_OPTIONS = [{
  key: '',
  label: 'tickets.filter.all'
}, {
  key: 'open',
  label: 'ticket.status.open_plural'
}, {
  key: 'closed',
  label: 'ticket.status.closed_plural'
}];
/**
 * Filter popup body. Selections are buffered in a local draft and
 * committed to the parent (triggering a backend request) on Apply
 * (which also closes the popup) or Reset (which stays open with a
 * cleared draft so the user can build a new filter).
 */
var AdvancedSearchPanelContent = function AdvancedSearchPanelContent(_ref) {
  var _STATE_OPTIONS$find, _draft$tagKeysOr;
  var filters = _ref.filters,
    onApply = _ref.onApply,
    onClose = _ref.onClose;
  var _useAppContext = useAppContext(),
    i18next = _useAppContext.i18next;
  var dispatch = useAppDispatch();
  var _useState = useState(filters),
    _useState2 = _slicedToArray(_useState, 2),
    draft = _useState2[0],
    setDraft = _useState2[1];

  // Optimistic default: until the catalog request settles, assume
  // tags exist so the section stays active. Once we have a response,
  // gate dimming on the actual content.
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    tagsFetched = _useState4[0],
    setTagsFetched = _useState4[1];
  useEffect(function () {
    dispatch(fetchAllAvailableTags())["finally"](function () {
      return setTagsFetched(true);
    });
  }, [dispatch]);

  // Partial-update helper — only one filter field changes per
  // event; the local draft always carries a complete filter set.
  var updateFilter = useCallback(function (key, value) {
    setDraft(function (prev) {
      return _objectSpread(_objectSpread({}, prev), {}, _defineProperty({}, key, value));
    });
  }, []);
  var currentStateOption = (_STATE_OPTIONS$find = STATE_OPTIONS.find(function (o) {
    var _draft$state;
    return o.key === ((_draft$state = draft.state) !== null && _draft$state !== void 0 ? _draft$state : '');
  })) !== null && _STATE_OPTIONS$find !== void 0 ? _STATE_OPTIONS$find : STATE_OPTIONS[0];
  var hasAnyDraftFilter = hasAnyAdvancedFilterActive(draft);
  var hasAnyAppliedFilter = hasAnyAdvancedFilterActive(filters);
  var handleApply = useCallback(function () {
    onApply(draft);
    onClose === null || onClose === void 0 || onClose();
  }, [draft, onApply, onClose]);

  // Reset clears both the draft and the applied filters, but keeps
  // the popup open so the user can immediately build a new filter
  // from a clean slate.
  var handleReset = useCallback(function () {
    setDraft(EMPTY_FILTERS);
    onApply(EMPTY_FILTERS);
  }, [onApply]);
  var allTags = useAppSelector(selectAllTagsAvailableForTicketTagging);
  var noTagsAvailable = tagsFetched && allTags.size === 0;
  return /*#__PURE__*/_jsxs("div", {
    className: "flex flex-col gap-y-3",
    children: [/*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx("div", {
        className: "mb-1.5 text-xs font-bold text-gray-800",
        children: i18next.t('tickets.filter.assignee')
      }), /*#__PURE__*/_jsx(AssigneePicker, {
        value: draft.assignee,
        onChange: function onChange(v) {
          return updateFilter('assignee', v);
        }
      })]
    }), /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx("div", {
        className: "mb-1.5 text-xs font-bold text-gray-800",
        children: i18next.t('tickets.filter.status')
      }), /*#__PURE__*/_jsxs(Menu, {
        as: "div",
        className: "relative text-left",
        children: [/*#__PURE__*/_jsxs(MenuButton, {
          className: "hover:darken-10 relative flex w-full items-center rounded-sm bg-gray-200 py-2 ps-3 pe-9 text-sm text-gray-800 dark:bg-gray-100",
          children: [/*#__PURE__*/_jsx("span", {
            children: i18next.t(currentStateOption.label)
          }), /*#__PURE__*/_jsx("span", {
            className: "absolute end-2 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center text-gray-600",
            children: /*#__PURE__*/_jsx(Icon, {
              iconKey: "arrow_down",
              size: "1.5rem"
            })
          })]
        }), /*#__PURE__*/_jsx(MenuItems, {
          className: "scrollbar shadow-base scrollbar-gutter-stable absolute end-0 z-20 mt-2 max-h-72 w-full origin-top-right rounded-md bg-white py-2 ps-2 outline-gray-100 focus:outline dark:bg-gray-300",
          children: STATE_OPTIONS.map(function (option) {
            return /*#__PURE__*/_jsx(MenuItem, {
              onClick: function onClick() {
                return updateFilter('state', option.key === '' ? undefined : option.key);
              },
              dataTestId: "dropdown-option-".concat(option.key),
              children: /*#__PURE__*/_jsx("span", {
                children: i18next.t(option.label)
              })
            }, "mi-".concat(option.key));
          })
        })]
      })]
    }), /*#__PURE__*/_jsxs("div", {
      className: twMerge(noTagsAvailable && 'opacity-50'),
      children: [/*#__PURE__*/_jsx("div", {
        className: "mb-1.5 text-xs font-bold text-gray-800",
        children: i18next.t('tickets.filter.tags')
      }), /*#__PURE__*/_jsx(TagPicker, {
        value: (_draft$tagKeysOr = draft.tagKeysOr) !== null && _draft$tagKeysOr !== void 0 ? _draft$tagKeysOr : [],
        onChange: function onChange(next) {
          return updateFilter('tagKeysOr', next.length > 0 ? next : undefined);
        },
        disabled: noTagsAvailable
      })]
    }), /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx("div", {
        className: "mb-1.5 text-xs font-bold text-gray-800",
        children: i18next.t('tickets.filter.since')
      }), /*#__PURE__*/_jsx(SinceDatePicker, {
        value: draft.since,
        onChange: function onChange(v) {
          return updateFilter('since', v);
        }
      })]
    }), /*#__PURE__*/_jsx(Checkbox, {
      label: /*#__PURE__*/_jsxs("span", {
        className: "flex items-center gap-1.5",
        children: [i18next.t('tickets.filter.marked'), /*#__PURE__*/_jsx("span", {
          className: "size-2 shrink-0 rounded-full bg-red-500"
        })]
      }),
      checked: draft.marked,
      onChange: function onChange(_e, checked) {
        return updateFilter('marked', checked);
      }
    }), /*#__PURE__*/_jsxs("div", {
      className: "flex gap-x-2",
      children: [/*#__PURE__*/_jsx(Button, {
        color: "gray",
        disabled: !hasAnyDraftFilter && !hasAnyAppliedFilter,
        onClick: handleReset,
        className: "h-9 flex-1 rounded-md text-gray-700 disabled:bg-gray-100 disabled:text-gray-400",
        hideTextOnOnePanel: true,
        children: i18next.t('tickets.filter.reset')
      }), /*#__PURE__*/_jsx(Button, {
        color: "primary",
        onClick: handleApply,
        className: "h-9 flex-1 rounded-md",
        hideTextOnOnePanel: true,
        children: i18next.t('tickets.filter.apply')
      })]
    })]
  });
};
export default AdvancedSearchPanelContent;